/**
 * Paths for the loop's actions outside cubic. Each is a signed-in page under
 * `/action/auto-fix` (the pattern "Fix all with cubic" and "Re-trigger cubic"
 * use) that performs the action. Turning the loop on then opens the pull
 * request's fix session, where its run starts or it waits for the next review;
 * every other action sends the user back to the pull request.
 *
 * A surface that renders one of these links passes its own `source`, so the
 * href is deterministic per pull request *and* per surface. Both properties
 * matter: state refreshes find a surface's own link by exact href
 * (`replaceAutoFixLoopLinkLabel`) without touching another surface's, and
 * the action page reports which entry point people actually used.
 */
import type { AutoFixLoopActionPageSource } from './analytics';

import { buildGithubPullRequestUrl } from '@/lib/ai/code-review-service/re-review-link';

export const AUTO_FIX_LOOP_RETURN_TO_QUERY_PARAM = 'returnTo';

/**
 * `?autoFix=enable` on cubic's PR page turns the loop on once the page has
 * loaded its status, then drops the parameter. It only ever enables: a link
 * sent earlier (the Slack DM) must not turn a running loop off. The DM now
 * links the enable page (`buildAutoFixLoopEnablePath`); the PR page still
 * honors the DMs sent before it did.
 */
export const AUTO_FIX_LOOP_ENABLE_QUERY_PARAM = 'autoFix';
export const AUTO_FIX_LOOP_ENABLE_QUERY_VALUE = 'enable';

const PR_PAGE_PATH = /^\/pr\/[^/]+\/[^/]+\/pull\/[^/]+\/?$/;

/**
 * A PR page opened to enable the loop needs a signed-in viewer even where PR
 * pages are public: signed out, the page renders without the auto-fix card
 * (public repositories) or not at all (private ones). The proxy runs session
 * auth for these requests, which sends a signed-out visitor to sign-in with
 * the full URL, parameter included, as the return target.
 */
export function isAutoFixLoopEnablePageRequest(url: URL, method: string): boolean {
  return (
    method.toUpperCase() === 'GET' &&
    PR_PAGE_PATH.test(url.pathname) &&
    url.searchParams.get(AUTO_FIX_LOOP_ENABLE_QUERY_PARAM) === AUTO_FIX_LOOP_ENABLE_QUERY_VALUE
  );
}
export const AUTO_FIX_LOOP_SOURCE_QUERY_PARAM = 'source';

type LoopPrParams = {
  owner: string;
  repo: string;
  pullNumber: number;
};

type LoopLinkParams = LoopPrParams & {
  returnTo?: string;
  /** Omitted for the review footer, whose links predate the parameter. */
  source?: AutoFixLoopActionPageSource;
};

function actionPath(segments: string[], params: LoopLinkParams): string {
  const searchParams = new URLSearchParams({
    [AUTO_FIX_LOOP_RETURN_TO_QUERY_PARAM]: params.returnTo ?? buildGithubPullRequestUrl(params),
  });
  if (params.source && params.source !== 'footer') {
    searchParams.set(AUTO_FIX_LOOP_SOURCE_QUERY_PARAM, params.source);
  }
  return [
    '/action/auto-fix',
    ...segments,
    encodeURIComponent(params.owner),
    encodeURIComponent(params.repo),
    `${encodeURIComponent(String(params.pullNumber))}?${searchParams.toString()}`,
  ].join('/');
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Replaces the label of every markdown link pointing at `href`, so a surface
 * can refresh its own state in place. Only cubic writes these hrefs, and each
 * surface's href differs, so the rewrite never reaches another surface's link.
 */
export function replaceAutoFixLoopLinkLabel(body: string, href: string, label: string) {
  const pattern = new RegExp(`\\[[^\\]\\n]*\\]\\(${escapeRegExp(href)}\\)`, 'g');
  return body.replace(pattern, `[${label}](${href})`);
}

/** Toggle page: turns the loop on when it is off, and off when it is on. */
export function buildAutoFixLoopTogglePath(params: LoopLinkParams) {
  return actionPath(['pr'], params);
}

/**
 * Enable page: turns the loop on, and never off, for a link that may be followed after the loop
 * was turned on, such as a Slack DM's.
 */
export function buildAutoFixLoopEnablePath(params: LoopLinkParams) {
  return actionPath(['enable', 'pr'], params);
}

/** Retry page: re-dispatches an iteration after a failed one. */
export function buildAutoFixLoopRetryPath(params: LoopLinkParams) {
  return actionPath(['retry', 'pr'], params);
}

export function buildAutoFixLoopToggleUrl(baseUrl: string, params: LoopLinkParams) {
  return `${baseUrl}${buildAutoFixLoopTogglePath(params)}`;
}

/** UTM tags per surface that links to a run's fix session. */
const RUN_LINK_UTM = {
  github: { source: 'github', content: 'auto_fix_progress_comment' },
  check_run: { source: 'github', content: 'auto_fix_check_run' },
  pr_page: { source: 'cubic', content: 'auto_fix_sidebar' },
  code_pr_tab: { source: 'cubic', content: 'auto_fix_code_pr_tab' },
} as const;

export type AutoFixLoopRunLinkSource = keyof typeof RUN_LINK_UTM;

/**
 * Tags a link to a loop run (its fix session) with the linking surface's UTM parameters, so
 * analytics can tell the PR comment, the check run, the PR page and /code's PR tab apart.
 */
export function withAutoFixLoopLinkAttribution(runUrl: string, source: AutoFixLoopRunLinkSource) {
  let url: URL | undefined;
  try {
    url = new URL(runUrl);
  } catch {
    // Handled below alongside non-HTTP(S) schemes, with one clear error.
  }
  if (!url || (url.protocol !== 'http:' && url.protocol !== 'https:')) {
    throw new Error(
      `withAutoFixLoopLinkAttribution: runUrl must be an absolute HTTP(S) URL, got ${JSON.stringify(runUrl)}`,
    );
  }
  url.searchParams.set('utm_source', RUN_LINK_UTM[source].source);
  url.searchParams.set('utm_content', RUN_LINK_UTM[source].content);
  return url.toString();
}
